import { inject, injectable } from 'inversify';
import type {
    CancellationPolicyDefaults,
    CancellationPolicyDefaultsResponse,
    CancellationPolicyRequest,
    CancellationPolicyResponse,
    FlexOfferResponse,
} from '@bookingkit/api-v4/dist/models';
import { BookingKitApiFactoryInterface } from '@/models/Factories/BookingKitApiFactoryInterface';
import { BOOKINGKIT_API_FACTORY, SUPPLIER_STATE_SERVICE } from '@/bootstrap/ServiceProviders';
import { UseSupplierInterface } from '@/state/supplier';
import { CancellationPoliciesServiceError } from './Errors/CancellationPoliciesServiceError';

@injectable()
export class CancellationPoliciesService {
    private connection: ReturnType<BookingKitApiFactoryInterface['getConnection']>;

    private supplierId: string;

    constructor(
        @inject(BOOKINGKIT_API_FACTORY) factory: BookingKitApiFactoryInterface,
        @inject(SUPPLIER_STATE_SERVICE) useSupplierState: () => UseSupplierInterface,
    ) {
        const { supplier } = useSupplierState();
        if (!supplier.value?.id) {
            throw CancellationPoliciesServiceError.fromMissingSupplier();
        }
        this.supplierId = supplier.value.id;
        this.connection = factory.getConnection();
        this.listPolicies = this.listPolicies.bind(this);
        this.createPolicy = this.createPolicy.bind(this);
        this.listFlexOffers = this.listFlexOffers.bind(this);
        this.listFlexOfferPolicies = this.listFlexOfferPolicies.bind(this);
        this.attachFlexOfferPolicy = this.attachFlexOfferPolicy.bind(this);
        this.detachFlexOfferPolicy = this.detachFlexOfferPolicy.bind(this);
        this.readDefaults = this.readDefaults.bind(this);
        this.updateDefaults = this.updateDefaults.bind(this);
    }

    public async listPolicies(pageToken?: string): Promise<{
        data: CancellationPolicyResponse[];
        nextPageToken?: string;
    }> {
        const { supplierId } = this;
        const { data } = await this.connection.cancellationPoliciesList({ vendorId: supplierId, pageToken });
        if (!Array.isArray(data?.data)) {
            throw CancellationPoliciesServiceError.fromInvalidProperty('data.data', 'array', data?.data);
        }
        const metadata = (data?.metadata ?? {}) as { 'page-token'?: string };
        return { data: data.data, nextPageToken: metadata['page-token'] };
    }

    public async createPolicy(policy: CancellationPolicyRequest): Promise<CancellationPolicyResponse> {
        const { supplierId } = this;
        const { data } = await this.connection.cancellationPoliciesCreate({
            vendorId: supplierId,
            cancellationPolicyRequest: policy,
        });

        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            throw CancellationPoliciesServiceError.fromInvalidProperty('data', 'object', data);
        }
        return data;
    }

    public async listFlexOffers(pageToken?: string): Promise<{
        data: FlexOfferResponse[];
        nextPageToken?: string;
    }> {
        const { supplierId } = this;
        const { data } = await this.connection.listFlexOffers({ vendorId: supplierId, pageToken });
        if (!Array.isArray(data?.data)) {
            throw CancellationPoliciesServiceError.fromInvalidProperty('data.data', 'array', data?.data);
        }
        return { data: data.data, nextPageToken: data?.metadata?.['page-token'] };
    }

    public async listFlexOfferPolicies(flexOfferId: string): Promise<CancellationPolicyResponse[]> {
        const { supplierId } = this;
        const { data } = await this.connection.flexOfferCancellationPoliciesList({
            vendorId: supplierId,
            flexOfferId,
        });
        if (!Array.isArray(data?.data)) {
            throw CancellationPoliciesServiceError.fromInvalidProperty('data.data', 'array', data?.data);
        }
        return data.data;
    }

    public async attachFlexOfferPolicy(flexOfferId: string, cancellationPolicyId: string): Promise<void> {
        const { supplierId } = this;
        await this.connection.flexOfferCancellationPolicyAttach({
            vendorId: supplierId,
            flexOfferId,
            cancellationPolicyId,
        });
    }

    public async detachFlexOfferPolicy(flexOfferId: string, cancellationPolicyId: string): Promise<void> {
        const { supplierId } = this;
        await this.connection.flexOfferCancellationPolicyDetach({
            vendorId: supplierId,
            flexOfferId,
            cancellationPolicyId,
        });
    }

    public async readDefaults(): Promise<CancellationPolicyDefaultsResponse> {
        const { supplierId } = this;
        const { data } = await this.connection.cancellationPoliciesDefaultsRead({ vendorId: supplierId });
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            throw CancellationPoliciesServiceError.fromInvalidProperty('data', 'object', data);
        }
        return data;
    }

    public async updateDefaults(defaults: CancellationPolicyDefaults): Promise<CancellationPolicyDefaultsResponse> {
        const { supplierId } = this;
        const { data } = await this.connection.cancellationPoliciesDefaultsUpdate({
            vendorId: supplierId,
            cancellationPolicyDefaults: defaults,
        });
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            throw CancellationPoliciesServiceError.fromInvalidProperty('data', 'object', data);
        }
        return data;
    }
}

export default CancellationPoliciesService;
